import type { PlaybookSectionNode } from "@repo/app-types/entities/playbook";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import { DiffableField } from "../diffableField.js";

export interface SectionFormProps {
  section: PlaybookSectionNode;
  liveSection?: PlaybookSectionNode;
  onUpdateSection: (updates: Partial<PlaybookSectionNode>) => void;
  isReadOnly?: boolean;
  /**
   * Fill the parent's height, with the description scrolling inside it. For a
   * parent whose height is bounded and doesn't scroll itself.
   */
  fillsHeight?: boolean;
  showsDescriptionHint?: boolean;
}

export const SectionForm: FC<SectionFormProps> = ({
  section,
  liveSection,
  onUpdateSection,
  isReadOnly = false,
  fillsHeight = false,
  showsDescriptionHint = true,
}) => {
  const currentDescription = section.description ?? "";

  return (
    <div
      className={twMerge(
        "space-y-4",
        fillsHeight && "flex min-h-0 flex-1 flex-col",
      )}
    >
      <div>
        <DiffableField
          label={<Label size={Size.SMALL}>Name</Label>}
          originalValue={liveSection?.name}
          value={section.name}
          onChange={(name) => {
            onUpdateSection({ name });
          }}
          placeholder="Introduction and compliance disclaimers"
          isReadOnly={isReadOnly}
          maxLength={300}
          variant="input"
        />
      </div>

      <div className={fillsHeight ? "flex min-h-0 flex-1 flex-col" : undefined}>
        <DiffableField
          label={<Label size={Size.SMALL}>Description</Label>}
          originalValue={
            liveSection ? (liveSection.description ?? "") : undefined
          }
          value={currentDescription}
          onChange={(description) => {
            onUpdateSection({ description });
          }}
          placeholder="Describe this section..."
          isReadOnly={isReadOnly}
          maxLength={2000}
          isMarkdown
          hasDiffMinimap
          editorClassName={fillsHeight ? "min-h-0 flex-1" : undefined}
        />
        {showsDescriptionHint ? (
          <p className="mt-2 shrink-0 text-sm text-gray-600">
            (Optional) This description helps the AI grader understand when to
            grade or skip this section.
          </p>
        ) : null}
      </div>
    </div>
  );
};
